import Actions from 'flow/actions';
import { CTI_HOST_DESCRIPTION } from 'flow/utils/cti-config-utils';
import { getIntegrationHost } from 'flow/utils/is-integration';
import { fromJS } from 'immutable';
import { getSelectedCtiContactByNumberFromIntegration } from 'flow/selectors/cti-integration-selectors';

const initialStateDefault = fromJS({
    integrationContacts: {},
    callLogSent: false,
    targetOrigin: null,
    activeCall: null,
    loggedIn: null,
    tooManyInstances: false,
    integrationConfig: {
        hostImgUrl: null,
        hostDescription: CTI_HOST_DESCRIPTION.DEFAULT,
        defaultButtons: [],
    },
});

const getInitialState = () => {
    const integrationHost = getIntegrationHost();
    const defaultHostName = integrationHost
        ? integrationHost
        : CTI_HOST_DESCRIPTION.DEFAULT;

    return initialStateDefault.setIn(
        ['integrationConfig', 'hostDescription'],
        defaultHostName,
    );
};

function mapLookupContactToIntegrationContact(lookupContact, contactSource) {
    const {
        typeLabel,
        itemLinkFlow,
        routeUrl,
        itemNameFlow,
        otherRoutesFlow,
        routeFlow,
        numberE164Flow,
        nameFlow,
        contactRouteButtons,
        altRouteFlow,
    } = lookupContact;

    return {
        // Integration specific.
        ...(lookupContact.metadata && { metadata: lookupContact.metadata }),
        ...(contactSource && { contactSource }),
        ...(typeLabel && { typeLabel }),
        ...(contactRouteButtons && { contactRouteButtons }),
        ...(otherRoutesFlow && { otherRoutes: otherRoutesFlow }),
        ...(routeUrl && { routeUrl }),
        ...(routeFlow && { contactRoute: routeFlow }),
        ...(itemLinkFlow && { itemLink: itemLinkFlow }), // LEGACY
        ...(itemNameFlow && { itemName: itemNameFlow }), // LEGACY
        ...(altRouteFlow && { altContactRoute: altRouteFlow }),

        // Contact DTO.
        firstName: nameFlow,
        number: numberE164Flow,
        key: 'contact-000000', // Needed to comply with prop-checks.

        isSelected: false,
    };
}

const sanitizeUrl = (urlString) => {
    try {
        const url = new URL(urlString);
        return url.protocol === 'https:' ? urlString : null;
    } catch (_) {
        return null;
    }
};

export const selectContacts = (state, number, contacts) => {
    const selectedCtiContact = getSelectedCtiContactByNumberFromIntegration(
        state,
        number,
    );
    const contactRoute = selectedCtiContact
        ? selectedCtiContact.get('contactRoute')
        : null;

    // if there is a previously selected contact, we want to find its equal, and make it selected
    const matchingContact = contacts.find(
        (contact) => contactRoute === contact.contactRoute,
    );
    if (matchingContact) {
        matchingContact.isSelected = true;
    } else if (contacts.length > 0) {
        // if there was no selected contact for the number, we want select the first contact.
        const firstContact = contacts[0];
        firstContact.isSelected = true;
    }
    return [number, contacts];
};

export const integrationReducer = (state = getInitialState(), action) => {
    switch (action.type) {
        case Actions.CLEAR_STATE: {
            return getInitialState();
        }

        case Actions.CTI_USER_LOGGED_OUT: {
            return state.set('loggedIn', false);
        }
        case Actions.CTI_USER_LOGGED_IN: {
            return state.set('loggedIn', true);
        }

        case Actions.CTI_RESULT_CONTACT_DECORATIONS: {
            const { contacts } = action.data;
            const contactSource = state.getIn([
                'integrationConfig',
                'hostDescription',
            ]);

            return Object.entries(contacts)
                .map(([number, contacts]) => [
                    number,
                    contacts.map((e) =>
                        mapLookupContactToIntegrationContact(e, contactSource),
                    ),
                ])
                .map(([number, contacts]) =>
                    selectContacts(state, number, contacts),
                ) // sets a selected contact for each number
                .reduce(
                    (accumulator, [number, contacts]) =>
                        accumulator.setIn(
                            ['integrationContacts', number],
                            fromJS(contacts),
                        ),
                    state,
                );
        }

        case Actions.CTI_REGISTER_ORIGIN: {
            const { targetOrigin, data } = action;
            const { host } = data ? data : {};
            const { imageUrl, description, defaultButtons, ctiHostSetting } =
                host ? host : {};
            if (ctiHostSetting) {
                state = state.setIn(
                    ['integrationConfig', 'ctiHostSetting'],
                    fromJS(ctiHostSetting),
                );
            }
            return state
                .set('targetOrigin', targetOrigin)
                .setIn(
                    ['integrationConfig', 'hostImgUrl'],
                    sanitizeUrl(imageUrl),
                )
                .setIn(['integrationConfig', 'hostDescription'], description)
                .setIn(
                    ['integrationConfig', 'defaultButtons'],
                    fromJS(defaultButtons || []),
                );
        }
        case Actions.CTI_FEATURE_FLAGS: {
            const { creationButtons, formButtons } = action.data;

            const featureButtons = {
                creationButtons: creationButtons,
                formButtons: formButtons,
            };

            return state.set('featureButtons', fromJS(featureButtons));
        }
        case Actions.CTI_CALL_LOG_SENT: {
            return state.set('callLogSent', true);
        }

        case Actions.CTI_CLEAR_CALL_LOG_SENT: {
            return state.set('callLogSent', false);
        }
        case Actions.CTI_SET_TOO_MANY_INSTANCES: {
            const { tooManyInstances } = action.data;
            return state.set('tooManyInstances', tooManyInstances);
        }

        case Actions.CTI_SET_SELECTED_CONTACT: {
            const number = action.contact.get('number');
            const contacts = state.getIn(['integrationContacts', number]);
            const newSelectContactIndex = contacts.findIndex(
                (contact) =>
                    contact.get('contactRoute') ===
                    action.contact.get('contactRoute'),
            );

            // toggles isSelected to false for all contacts
            state = state.updateIn(
                ['integrationContacts', number],
                null,
                (contacts) => {
                    return contacts.map((contact) =>
                        contact.set('isSelected', false),
                    );
                },
            );

            // updates isSelected to true for the new selected contact
            return state.updateIn(
                ['integrationContacts', number, newSelectContactIndex],
                (contact) => contact.set('isSelected', true),
            );
        }

        default: {
            return state;
        }
    }
};

export default integrationReducer;
